<template>
    <layout>
        <main class="identify-page">
            <section class="header">
                <div class="container">
                    <h2>2. Brace Name &amp; Seismic Coefficient (C<sub>p</sub>)</h2>
                    <p>
                        Name your brace, enter a drawing reference and choose a method below for finding the Seismic Coefficient for your project.
                    </p>
                    <div v-if="!hasAgency && designId" class="required">
                        AGENCY SELECTION REQUIRED: Please go back to
                        <router-link :to="{ name: 'submittal-setup', params: {id: designId}}">Seismic Design Setup</router-link>
                        and select an agency before proceeding for accurate options here.
                    </div>
                </div>
            </section>
            <section>
                <div class="container">
                    <h3>Identify your Brace</h3>
                    <fieldset class="flex notes">
                        <div class="triple">
                            <div class="text">
                                <label for="brace-name">Brace Name</label>
                                <input type="text" v-model="record.name" id="brace-name" placeholder="Name your brace" required>
                                <errors :errors="errors" prop="name"></errors>
                            </div>
                            <div class="text">
                                <label for="drawing-ref">Drawing Reference</label>
                                <input type="text" v-model="record.drawingReference" id="drawing-ref" placeholder="Enter reference">
                                <errors :errors="errors" prop="drawingReference"></errors>
                            </div>
                            <div class="text">
                                <label for="quantity">Quantity</label>
                                <input type="number" v-model="record.quantity" id="quantity" min="1">
                                <errors :errors="errors" prop="quantity"></errors>
                                <div class="note">for Bill of Materials</div>
                            </div>
                        </div>
                    </fieldset>
                </div>
            </section>
            <section class="cp-calc">
                <div class="container">
                    <h3>Find Seismic Coefficient (C<sub>p</sub>)</h3>
                    <p>
                        Pick a method that best suits your project needs.
                    </p>

                    <errors :errors="errors" prop="cp"></errors>

                    <fieldset>
                        <div class="radio">
                            <div v-if="showCalcOption('ss')">
                                <input type="radio" id="project-value" value="ss" v-model="record.calculationMethod" name="calc" @change="toggleCalculationMethod">
                                <label for="project-value">
                                    Enter Your Project’s S<sub>s</sub> Value
                                    <span class="note" v-text="ssText"></span>
                                </label>
                            </div>

                            <div v-if="showCalcOption('cp')">
                                <input type="radio" id="project-cp" value="cp" v-model="record.calculationMethod" name="calc" @change="toggleCalculationMethod">
                                <label for="project-cp">
                                    Enter Your Project’s C<sub>p</sub>
                                </label>
                            </div>

                            <div v-if="showCalcOption('sds')">
                                <input type="radio" id="project-sds" value="sds" v-model="record.calculationMethod" name="calc" @change="toggleCalculationMethod">
                                <label for="project-sds">
                                    Enter Your Project’s S<sub>DS</sub>
                                </label>
                            </div>

                            <div v-if="showCalcOption('asce')">
                                <input type="radio" id="calculate-cp" value="asce" v-model="record.calculationMethod" name="calc" @change="toggleCalculationMethod">
                                <label for="calculate-cp">
                                    Calculate Your Project’s C<sub>p</sub> Per <span v-text="cpStandard"></span> <span class="note" v-text="cpText"></span>
                                </label>
                            </div>

                            <div v-if="showCalcOption('tia')">
                                <input type="radio" id="calculate-tia" value="tia" v-model="record.calculationMethod" name="calc" @change="toggleCalculationMethod">
                                <label for="calculate-tia">
                                    Calculate Your Project’s C<sub>p</sub> Per <span v-text="tiaStandard"/> <span class="note" v-text="tiaText"/> 
                                </label>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset v-if="calc === 'ss'" class="flex notes full-input">
                        <div>
                            <ss-field v-model="ss" :error="ssError" />
                            <div></div>
                        </div>
                        <div v-if="showNfpaSiteOptions">
                            <nfpa-height-field :value="h" @change="setH" />
                            <div class="text">
                                <label class="required" for="site">
                                    Site
                                </label>
                                <div class="select">
                                    <select @click="" v-model="record.site" id="site" required placeholder="Select your Site value">
                                        <option v-for="site in nfpaSites" :value="site.value" v-text="site.name"></option>
                                    </select>
                                </div>
                                <p class="note">Site Class per <span v-text="standardName"></span></p>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset v-if="calc === 'asce'" class="flex notes full-input">
                        <div class="help">
                            <modal tooltip="click for help with ASCE" :result="pdfFilePath" text="" link-classes="fa fa-question"/>
                        </div>
                        <div>
                            <ss-field v-model="ss" :error="ssError" />
                            <div class="text">
                                <label class="required" for="site">
                                    Site
                                </label>
                                <div class="select">
                                    <select v-model="record.site" id="site" required placeholder="Select your Site value">
                                        <option v-for="site in calculationSites" :value="site.value" v-text="site.name"></option>
                                    </select>
                                </div>
                                <p class="note">Site Class per ASCE/SEI 7</p>
                            </div>
                        </div>

                        <div>
                            <div class="text">
                                <label class="required" for="z">
                                    z ({{ useMetric(2) ? 'm.' : 'ft.' }})
                                </label>
                                <input-number key="input-z" :mToFt="useMetric(2)" v-model="z" id="z" placeholder="Enter your z value" :min="0" step=".1" required />
                                <p class="note">Height of point of attachment from base</p>
                            </div>

                            <div class="text">
                                <label class="required" for="h">
                                    h ({{ useMetric(2) ? 'm.' : 'ft.' }})
                                </label>
                                <input-number key="input-h" :mToFt="useMetric(2)" v-model="h" id="h" placeholder="Enter your h value" :min="0" step=".1" required />
                                <p class="note">Avg roof structure height with respect to base</p>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset v-if="calc === 'cp'" class="flex notes">
                        <div>
                            <div class="text flex">
                                <label class="required" for="cp">
                                    C<sub>p</sub>
                                </label>
                                <input-number key="cp-input" v-model="record.cp" id="cp" placeholder="Enter your Cp value" :min="0" step=".1" required />
                            </div>
                        </div>
                    </fieldset>

                    <fieldset v-if="calc === 'sds'" class="flex notes full-input">
                        <div>
                            <sds-field v-model="sds" :error="ssError" />
                            <nfpa-height-field v-model="h" @change="setH" />
                        </div>
                    </fieldset>

                    <fieldset v-if="calc === 'tia'" class="flex notes display-conditional">
                        <div>
                            <div class="text">
                                <label class="required" for="z">
                                    z ({{ useMetric(2) ? 'm.' : 'ft.' }})
                                </label>
                                <input-number key="input-z" :mToFt="useMetric(2)" v-model="z" id="z" placeholder="Enter your z value" :min="0" step=".1" required />
                                <p class="note">Height of point of attachment from base</p>
                            </div>

                            <div class="text">
                                <label class="required" for="h">
                                    h ({{ useMetric(2) ? 'm.' : 'ft.' }})
                                </label>
                                <input-number key="input-h" :mToFt="useMetric(2)" v-model="h" id="h" placeholder="Enter your h value" :min="0" step=".1" required />
                                <p class="note">Avg roof structure height with respect to base</p>
                            </div>
                        </div>
                        <div>
                            <sds-field v-model="sds" :error="ssError" />
                            <div class="text">
                                <label class="required">
                                    Building Risk Category
                                </label>
                                <div class="select">
                                    <select v-model="record.riskCategory" id="site" required placeholder="Select Risk Category">
                                        <option v-for="cat in riskCategories" :value="cat.value" v-text="cat.name"></option>
                                    </select>
                                </div>
                                <p class="note">Risk category of building per ASCE 7-22 Table 1.5-1</p>
                            </div>
                        </div>
                        <force-resisting-system 
                            :standardId="standardId" 
                            :system="record.system" 
                            :subsystem="record.subsystem"  
                            :r="record.rValue" 
                            :omega="record.omega"
                            :manualInputPropValues="record.manualInputValues"
                            @set-system-params="setSystemParams"
                        />
                    </fieldset>

                    <!-- This text block is for testing only. It can probably be removed after the TIA calculation has been live for a while -->
                    <div class="info" v-if="showDebugging">
                        <h4>Testing variables</h4>
                        <p>
                            System: <strong v-text="record.system"/>
                        </p>
                        <p>
                            Subsystem: <strong v-text="record.subsystem"/>
                        </p>
                        <p>
                            R Value: <strong v-text="record.rValue"/>
                        </p>
                        <p>
                            Omega Value: <strong v-text="record.omega"/>
                        </p>
                        <p>
                            Manual Input Values: <strong v-text="record.manualInputValues"/>
                        </p>
                        <p>
                            Risk Category: <strong v-text="record.riskCategory"/>
                        </p>
                        <p>
                            Site: <strong v-text="record.site"/>
                        </p>
                        <p>
                            SS: <strong v-text="record.ss"/>
                        </p>
                        <p>
                            SDS: <strong v-text="record.sds"/>
                        </p>
                        <p>
                            Z: <strong v-text="record.z"/>
                        </p>
                        <p>
                            Record H: <strong v-text="record.h"/>
                        </p>
                        <p>
                            Data H: <strong v-text="h"/>
                        </p>
                    </div>

                    <div class="cp-value" v-if="calc">
                        C<sub>p</sub> =
                        <span v-if="calculating">
                            <i class="fa fa-refresh fa-spin fa-fw"></i>
                            Calculating
                        </span>
                        <span v-else-if="!validSite">
                            Please enter your project's C<sub>p</sub> manually.
                        </span>
                        <span v-else-if="!canCalculate || !validCp">
                            Enter parameters
                        </span>
                        <span v-else-if="cp === false">
                            Invalid parameters. Please revise.
                        </span>
                        <span v-else v-text="cp"></span>
                    </div>
                </div>
                <div v-if="validationError" class="text-right">
                    <span class="error" v-text="validationError"></span>
                </div>
            </section>

            <section>
                <actions
                    @next="saveThenNext"
                    @save="saveThenSummary"></actions>
            </section>
        </main>
        <aside class="primary">
            <sidebar :design="this.designRecord" :brace="this.record"></sidebar>
        </aside>
    </layout>
</template>

<script>
    import InputNumber from 'vue/common/_inputNumber';
    import ClockvineMixin from 'vue/common/clockvine-mixin';
    import SubmittalDesignLoader from './mixins/submittal-design-loader';
    import Modal from './_modal';

    import Layout from './layout';

    import Errors from './shared/errors';
    import Sidebar from './shared/sidebar';
    import Actions from './shared/actions';

    import ssField from './components/ss-field';
    import sdsField from './components/sds-field';
    import nfpaHeightField from './components/nfpa-height-field';
    import forceResistingSystem from './components/force-resisting-system';

    import { mapGetters } from 'vuex';
    import axios from 'axios';
    import { debounce } from 'lodash';

    export default {
        components: {
            Layout,
            Sidebar,
            Actions,
            Errors,
            InputNumber,
            Modal,
            ssField,
            sdsField,
            nfpaHeightField,
            forceResistingSystem,
        },

        mixins: [
            ClockvineMixin,
            SubmittalDesignLoader
        ],

        data() {
            return {
                type: 'submittalBrace',
                record: {
                    designId: this.designId,
                    calculationMethod: null,
                    site: null,
                    cp: null,
                    ss: null,
                    z: null,
                    h: null,
                    sds: null,
                    riskCategory:null,
                    system: null,
                    subsystem: null,
                    rValue:null,
                    omega:null,
                    manualInputValues: []
                },

                ss: null,
                h: null,
                z: null,
                sds: null,

                calculating: false,
                validationError: null,

                nfpaSites: [
                    { name: 'Default', value: 'default' },
                    { name: 'A - Hard Rock', value: 'a' },
                    { name: 'B - Rock', value: 'b' },
                    { name: 'C - Very Dense Soil and Soft Rock', value: 'c' },
                    { name: 'D - Stiff Soil', value: 'd' },
                    { name: 'E - Soft Clay Soil', value: 'e' },
                    { name: 'F - Others - Site Specific Evaluation Required', value: 'f' },
                ],
                calculationSites: [
                    { name: 'A - Hard Rock', value: 'a' },
                    { name: 'B - Rock', value: 'b' },
                    { name: 'C - Very Dense Soil and Soft Rock', value: 'c' },
                    { name: 'D - Stiff Soil', value: 'd' },
                    { name: 'E - Soft Clay Soil', value: 'e' },
                    { name: 'F - Others - Site Specific Evaluation Required', value: 'f' },
                ],
                riskCategories: [
                    { name: 'I', value: 'I' },
                    { name: 'II', value: 'II' },
                    { name: 'III', value: 'III' },
                    { name: 'IV', value: 'IV' },
                ],
                showDebugging: false, // Used for testing calculation params
            }
        },
        mounted() {
            this.$store.dispatch('submittalSystem/fetchAll', { id: this.id })
                .then(() => {
                    this.prepopulateCpFields();
                });
        },
        watch: {
            ss(value) {
                this.record.ss = this.numberOrNothing(value, 3).toString();
                this.calculateCp();
            },
            z(value) {
                this.record.z = this.numberOrNothing(value, 3).toString();
                this.calculateCp();
            },
            h(value) {
                this.record.h = this.numberOrNothing(value, 3).toString();
                this.calculateCp();
            },
            sds(value) {
                this.record.sds = this.numberOrNothing(value, 3).toString();
                this.calculateCp();
            },
            "record.site"() {
                this.calculateCp();
            },
            "record.riskCategory"() {
                if (this.calc == 'tia') {
                    this.calculateCp();
                }
            },
            "record.rValue"() {
                if (this.calc == 'tia') {
                    this.calculateCp();
                }
            },
            "record.omega"() {
                if (this.calc == 'tia') {
                    this.calculateCp();
                }
            },
            "record.manualInputValues"(value) {
                if (typeof value === 'string') {          
                    const values = this.asArray(value);
                    this.record.manualInputValues = values;
                }
            },
            calc() {
                this.calculateCp();
            },
            record() {
                this.ss = this.numberOrNothing(this.record.ss);
                this.sds = this.numberOrNothing(this.record.sds);
                this.h = this.numberOrNothing(this.record.h);
                this.z = this.numberOrNothing(this.record.z);
            },
        },
        computed: {
            ...mapGetters({
                standardId: 'submittalStandards/id',
                standardName: 'submittalStandards/standardName',
                lookupStandardId: 'submittalStandards/standardId',
            }),
            showNfpaSiteOptions() {
                if (this.standardId == 3) {
                    return true;
                }
                return false;
            },
            calc() {
                return this.record.calculationMethod;
            },
            cp() {
                return this.record.cp;
            },
            validCp() {
                if (this.cp) {
                    return true;
                }
                return false;
            },
            validSite() {
                if (this.calc == 'asce') {
                    return this.record.site != 'f';
                }
                if (this.calc == 'ss') {
                    // for NFPA 13-2016 and -2019 we don't use the site drop down
                    if (this.standardId <= 2) {
                        return true;
                    }
                    return ['e', 'f'].includes(this.record.site) ? false : true;
                }
                return true;
            },
            ssError() {
                if (this.ss === null) {
                    return null;
                }

                let ss = this.numberOrNothing(this.ss);
                let sds = this.numberOrNothing(this.sds);

                if (this.calc === 'ss' && (ss < 0.0001 || ss > 4.000)) {
                    return "Please enter an S<sub>s</sub> value between 0.001 and 4.000";
                } else if (this.calc === 'asce' && (ss < 0.0001 || ss > 4.500)) {
                    return "Please enter an S<sub>s</sub> value between 0.001 and 4.500";
                } else if (this.calc == 'sds' && sds && (sds < 0.0001 || sds > 4.000)) {
                    return "Please enter an S<sub>DS</sub> value between 0.001 and 4.000";
                } else if (this.calc == 'tia' && sds && (sds < 0.0001 || sds > 4.000)) {
                    return "Please enter an S<sub>DS</sub> value between 0.001 and 4.000";
                }

                return null;
            },
            ssText() {
                if (this.standardId == 3) {
                    return '(Per NFPA 13-2022 Table 18.5.9.3)';
                } else if (this.standardId == 2) {
                    return '(Per NFPA 13-2019 Table 18.5.9.3)';
                } else {
                    return '(Per NFPA 13-2016 Table 9.3.5.9.3)';
                }
            },
            cpStandard() {
                if (this.standardId == 2) {
                    return 'ASCE/SEI 7-16';
                } else {
                    return 'ASCE/SEI 7-10';
                }
            },
            cpText() {
                if (this.standardId == 3) {
                    return '(Per NFPA 13-2022 Table 18.5.9.3)';
                } else if (this.standardId == 2) {
                    return '(Per NFPA 13-2019 Table 18.5.9.3)';
                } else {
                    return '(Per NFPA 13-2016 Section 9.3.5.9.4)';
                }
            },
            tiaStandard() {
                if (this.standardId == 4) {
                    return 'ASCE/SEI 7-22';
                }
            },
            tiaText() {
                if (this.standardId == 4) {
                    return '(13-2022 TIA 1747 Section 18.5.9.4)';
                }
            },
            pdfFilePath() {
                return '/docs/ASCE_7-10_7-16_per_NFPA-13-2019.pdf'; // Just return the new file for now. If necessary we can choose file based on standardId later

                // if (this.standardId == 2) {
                //     return '/docs/ASCE_7-10_7-16_per_NFPA-13-2019.pdf';
                // }
                // return '/docs/ASCE SEI 7-10 Seismic Horizontal Design Load Calculations per NFPA 13-2016.pdf';
            },
            canCalculateSs() {
                return (
                    this.calc == 'ss' &&
                    this.validSite &&
                    this.record.ss &&
                    !this.ssError
                );
            },
            canCalculateAsce() {
                return (
                    this.calc == 'asce' &&
                    this.validSite &&
                    this.record.ss &&
                    this.record.site &&
                    this.record.z &&
                    this.record.h &&
                    !this.ssError
                );
            },
            canCalculateSds() {
                return (
                    this.calc == 'sds' &&
                    this.record.sds &&
                    this.record.h &&
                    !this.ssError
                );
            },
            canCalculateTia() {
                return (
                    this.calc == 'tia' &&
                    this.record.sds &&
                    this.record.z &&
                    this.record.h &&
                    this.record.riskCategory &&
                    this.record.system &&
                    this.record.rValue &&
                    this.record.omega &&
                    !this.ssError
                );
            },
            canCalculate() {
                return (
                    this.calc == 'cp' ||
                    this.canCalculateSs ||
                    this.canCalculateAsce ||
                    this.canCalculateSds ||
                    this.canCalculateTia
                );
            },
            hasAgency() {
                return this.designRecord ? this.designRecord.agency : '';
            },
        },

        methods: {
            setSystemParams(params) {
                this.record.system = params.system;
                this.record.subsystem = params.subsystem;
                this.record.rValue = params.rValue;
                this.record.omega = params.omega;
                this.record.manualInputValues = params.manualInputValues;
            },
            calculateCp() {
                this.cleanUpCpFields();
                this.validationError = null;
                if (this.canCalculate) {
                    this.calculating = true;
                    this.calculateCpRequest();
                }
            },
            cleanUpCpFields() {
                // Unset values we don't need when toggling calc method
                if (this.calc == 'cp') {
                    this.ss = null;
                    this.sds = null;
                    this.h = null;
                    this.z = null;
                    this.record.site = null;
                }
                if (this.calc == 'ss') {
                    this.sds = null;
                    this.z = null;
                }
                if (this.calc == 'sds') {
                    this.ss = null;
                    this.z = null;
                    this.record.site = null;
                }
                if (this.calc != 'tia') {
                    this.record.riskCategory = null;
                    this.record.system = null;
                    this.record.subsystem = null;
                    this.record.rValue = null;
                    this.record.omega = null;
                    this.record.manualInputValues = [];
                }
            },
            calculateCpRequest: (debounce(function() {
                // Prevent the querystring from being too long when editing
                //  an existing record by unsetting unnecessary params
                let temp = {...this.record};
                temp.pipeAttachmentOptions = '';
                temp.structuralAttachmentOptions = '';

                temp.standardId = this.lookupStandardId;

                // 'this' isn't properly bound with shorthand function
                axios.get('/actions/seismic-submittal/brace/calculate-cp', {params: temp})
                    .then(r => {
                        this.record.cp = r.data.cp;
                    })
                    .catch(() => {
                        this.record.cp = null;
                        this.calculatedCp = false; 
                    })
                    .then(() => { this.calculating = false; });
            }, 300, {'leading': false})),

            getUrlParams() {
                return { designId: this.$route.params.designId };
            },

            prepopulateCpFields() {
                if (!this.record.id) {
                    axios.get('/actions/seismic-submittal/brace/last-brace-cp-settings', {params: {designId: this.record.designId}})
                    .then(r => {
                        this.ss = this.numberOrNothing(r.data.ss);
                        this.z = this.numberOrNothing(r.data.z);
                        this.h = r.data.h;
                        this.sds = this.numberOrNothing(r.data.sds);
                        this.riskCategory = r.data.riskCategory;
                        this.system = r.data.system;
                        this.subsystem = r.data.subsystem;
                        this.rValue = this.numberOrNothing(r.data.rValue);
                        this.omega = this.numberOrNothing(r.data.omega);

                        Object.assign(this.record, {
                            calculationMethod : r.data.calculationMethod,
                            site : r.data.site,
                            cp : this.numberOrNothing(r.data.cp),
                            ss : this.ss,
                            sds : this.sds,
                            z : this.z,
                            h : this.h,
                            riskCategory : this.riskCategory,
                            system : this.system,
                            subsystem : this.subsystem,
                            rValue : this.rValue,
                            omega : this.omega,
                            manualInputValues : r.data.manualInputValues,
                        });

                        this.calculateCp();
                    })
                    .catch(() => { 'error getting last brace settings' })
                }
            },

            saveThenSummary() {
                if (this.validSite) {
                    this.save().then(() => {
                        this.$router.push({name: 'submittal-brace-summary', params: { id: this.designId }});
                    });
                } else {
                    this.validationError = 'Invalid Seismic Coefficient (Cp)';
                }
            },

            saveThenNext() {
                if (this.validSite) {
                    this.save().then(() => {
                        this.$router.push({name: 'submittal-brace-piping', params: {
                            id: this.record.id,
                            designId: this.designId
                        }});
                    });
                } else {
                    this.validationError = 'Invalid Seismic Coefficient (Cp)';
                }
            },
            showCalcOption(method) {
                let methods = ['cp'];
                let sIds = [1,2,3,4];

                if (methods.includes(method) && sIds.includes(this.standardId)) {
                    return true;
                }

                methods = ['ss', 'asce'];
                sIds = [1,2,3];

                if (methods.includes(method) && sIds.includes(this.standardId)) {
                    return true;
                }

                methods = ['sds', 'tia'];
                sIds = [4];

                if (methods.includes(method) && sIds.includes(this.standardId)) {
                    return true;
                }

                return false;
            },
            toggleCalculationMethod() {
                this.record.cp = '';
            },
            numberOrNothing(value, precision=null) {
                let val = parseFloat(value);
                if (isNaN(val)) {
                    return '';
                }
                if (precision) {
                    return val.toFixed(precision);
                }

                return val;
            },
            setH(val) {
                this.h = val;
            },
            asArray(value) {
                if (Array.isArray(value)) {
                    return value;
                } else if (typeof value === 'string') {
                    // If it's a string, try to parse it as JSON
                    try {
                        const parsed = JSON.parse(value);
                        if (Array.isArray(parsed)) {
                            return parsed;
                        } else {
                            console.error('The string is not a valid array');
                        }
                    } catch (e) {
                        console.error('Invalid JSON string');
                    }
                }
            }
        }
    }
</script>